import { useTripBuilder } from '../TripBuilderContext';
import bookingLinks from '../bookingLinks';

// Calendar-aware pre-departure checklist. Each task is scored against the
// number of days until departure so the user always knows "what should I
// be doing right now."
//
// Status emoji semantics:
//   ⏰ — overdue or due now
//   📅 — coming up soon (within the next two weeks)
//   ✅ — already handled (we surface it but it's checked off)
//   🕐 — too early to act (visible for awareness)
//
// Tasks reference the user's actual picks (hotels, restaurants, legs) so
// they get personalized "Book X by Y" reminders, not generic templates.
export default function PreDepartureTimeline() {
  const tb = useTripBuilder();
  if (!tb.startDate) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(tb.startDate + 'T12:00:00');
  const daysUntil = Math.ceil((start - today) / 86400000);

  // Generate per-window task list
  const tasks = [];

  // Task: confirm passport validity (always, very early)
  tasks.push({
    dueDays: 90,
    label: 'Confirm everyone\'s passport is valid for 6+ months beyond return date',
    why: 'Most countries (including all of Schengen) require 6 months passport validity. Renewing in the US takes 6-12 weeks.',
    category: 'documents',
    link: 'https://travel.state.gov/content/travel/en/passports.html',
    linkLabel: 'US Passport status',
  });

  // Task: research + confirm transferable currency balances
  tasks.push({
    dueDays: 60,
    label: 'Confirm your points balances are correct in PointsPilot Settings',
    why: 'Balances drift as you redeem points or earn new ones. The trip plan budget assumes the numbers in Settings.',
    category: 'planning',
  });

  // Task: book reservations for hot restaurants
  const allRestaurants = [
    ...(tb.restaurants || []),
    ...tb.additionalStops.flatMap(s => s.restaurants || []),
  ];
  const fineDining = allRestaurants.filter(r => r.category === 'fine_dining' || r.price_range === '$$$$');
  if (fineDining.length > 0) {
    tasks.push({
      dueDays: 35,
      label: `Book your ${fineDining.length} reservation${fineDining.length === 1 ? '' : 's'} at Michelin/tasting-menu spots (${fineDining.slice(0,2).map(r => r.name).join(', ')}${fineDining.length > 2 ? ', ...' : ''})`,
      why: 'Top-tier restaurants open reservations 30-60 days out and fill in minutes. Set a calendar reminder for the morning their booking window opens.',
      category: 'reservations',
    });
  }
  const otherRestaurants = allRestaurants.filter(r => r.category !== 'fine_dining' && r.price_range !== '$$$$');
  if (otherRestaurants.length > 0) {
    tasks.push({
      dueDays: 14,
      label: `Book your ${otherRestaurants.length} other restaurant reservation${otherRestaurants.length === 1 ? '' : 's'}`,
      why: 'Local favorites fill the prime 7-9 PM window 1-2 weeks ahead. Off-peak slots open up later if needed.',
      category: 'reservations',
    });
  }

  // Task: transfer points for flights (if not yet picked, prompts to pick; if picked, prompts to confirm + transfer)
  const flightsPicked = tb.isMultiCity
    ? Object.values(tb.flightsByLeg || {}).filter(Boolean)
    : (tb.flight ? [tb.flight] : []);

  if (flightsPicked.length === 0) {
    tasks.push({
      dueDays: 90,
      label: 'Pick your flights on the Plan Trip → Flights step',
      why: 'Award space tightens 60-90 days before departure. Book first, transfer points only AFTER you see seats on the airline\'s site.',
      category: 'flights',
      urgent: true,
    });
  } else {
    flightsPicked.forEach(f => {
      const src = f.source_program === 'chase_ur' ? 'chase_ur' : 'amex_mr';
      tasks.push({
        dueDays: 14,
        label: `Re-verify ${f.airline_program} award seats on the airline's site (${f.routing_description}), then transfer ${tb.scaleForTravelers(f.total_points_two_travelers).toLocaleString()} ${src === 'chase_ur' ? 'Chase UR' : 'Amex MR'} and book`,
        why: 'Award seats can disappear. ALWAYS confirm seats are still showing in your name + dates BEFORE transferring. Transfers are one-way.',
        category: 'flights',
        link: bookingLinks[src]?.transfer,
        linkLabel: `Open ${src === 'chase_ur' ? 'Chase' : 'Amex'} transfers`,
      });
    });
  }

  // Task: hotels — transfer + book per stop
  const allStopsWithHotel = tb.stops.filter(s => s.hotel);
  const stopsMissingHotel = tb.stops.filter(s => !s.hotel);
  if (stopsMissingHotel.length > 0) {
    tasks.push({
      dueDays: 60,
      label: `Pick hotels for ${stopsMissingHotel.length} remaining ${stopsMissingHotel.length === 1 ? 'city' : 'cities'} (${stopsMissingHotel.map(s => s.destination.city).join(', ')})`,
      why: 'Best Hyatt + Hilton + Marriott award space goes fast in popular European cities. Pick + book by 6 weeks out.',
      category: 'hotels',
      urgent: true,
    });
  }
  allStopsWithHotel.forEach(s => {
    const hSrc = s.hotel.source_program === 'chase_ur' ? 'chase_ur' : 'amex_mr';
    tasks.push({
      dueDays: 7,
      label: `Transfer ${s.hotel.total_points?.toLocaleString()} ${hSrc === 'chase_ur' ? 'Chase UR' : 'Amex MR'} → ${s.hotel.hotel_program} and book ${s.hotel.hotel_name} in ${s.destination.city}`,
      why: 'Hotel award space tightens for September trips. Book at least 1 week out to lock in your category. If the property has free cancellation, book even earlier.',
      category: 'hotels',
      link: bookingLinks[hSrc]?.transfer,
      linkLabel: `Open ${hSrc === 'chase_ur' ? 'Chase' : 'Amex'} transfers`,
    });
  });

  // Task: travel insurance
  tasks.push({
    dueDays: 30,
    label: 'Buy travel insurance (or confirm your credit card\'s coverage is sufficient)',
    why: 'Most premium travel cards (Amex Platinum, Sapphire Reserve, CSP, Cap One Venture X) include trip delay + medical evacuation, but check coverage limits before relying on them. Add explicit medical evac coverage if going somewhere remote.',
    category: 'documents',
  });

  // Task: download offline content
  tasks.push({
    dueDays: 7,
    label: 'Download offline maps (Google Maps) + offline translations + your boarding pass to the wallet app',
    why: 'European data roaming is expensive; offline maps + translations save you. Wallet boarding pass + hotel confirmations work without signal.',
    category: 'logistics',
  });

  // Task: notify bank + activate cards for international use
  tasks.push({
    dueDays: 7,
    label: 'Notify your bank about international travel + confirm your cards have no foreign transaction fees',
    why: 'Many banks auto-decline international charges without a travel notice. Foreign transaction fees (3% typical) add up — use only no-FTF cards (Amex Platinum, CSR, CSP, Cap One Venture).',
    category: 'logistics',
  });

  // Task: check in for flights
  if (flightsPicked.length > 0) {
    tasks.push({
      dueDays: 1,
      label: 'Check in for all flight legs online 24 hours before each departure',
      why: 'Online check-in opens 24 hours out for most airlines. Aeroplan/Lufthansa let you pick better seats at this point. Skip the airport check-in queue.',
      category: 'flights',
    });
  }

  // Task: pack + final logistics
  tasks.push({
    dueDays: 3,
    label: 'Pack using the auto-generated packing list (Trip view → Packing tab)',
    why: 'The packing list adapts to your activities (kayak, hike, fine dining) and destination weather. Print it or check off in the app the day before.',
    category: 'logistics',
  });

  // Sort: most urgent (due today/past due) first
  tasks.sort((a, b) => {
    const aDue = daysUntil - a.dueDays;
    const bDue = daysUntil - b.dueDays;
    return bDue - aDue;
  });

  function statusFor(task) {
    // task.dueDays = "how many days before trip start this task should happen"
    // daysUntil    = days remaining until the trip departs
    if (daysUntil <= task.dueDays && daysUntil > task.dueDays - 7) {
      return { emoji: '⏰', label: 'Due now', color: 'bg-rose-500/15 text-rose-300 border-rose-500/30' };
    }
    if (daysUntil < task.dueDays - 7) {
      return { emoji: '🔥', label: 'Past due', color: 'bg-rose-500/25 text-rose-200 border-rose-500/50' };
    }
    if (daysUntil <= task.dueDays + 14) {
      return { emoji: '📅', label: `In ${daysUntil - task.dueDays}d`, color: 'bg-gold/15 text-gold border-gold/30' };
    }
    return { emoji: '🕐', label: 'Later', color: 'bg-brand-ink/10 text-brand-ink/55 border-brand-ink/15' };
  }

  return (
    <div className="bg-navy-light border border-brand-ink/10 rounded-2xl p-4 sm:p-5 space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-lg font-bold">Pre-departure timeline</h3>
        <div className="text-xs text-brand-ink/65">
          {daysUntil > 0
            ? `${daysUntil} days until departure`
            : daysUntil === 0 ? 'Today!' : `Trip started ${-daysUntil} days ago`}
        </div>
      </div>

      <div className="space-y-2">
        {tasks.map((t, i) => {
          const status = statusFor(t);
          return (
            <div key={i} className="flex items-start gap-2.5 bg-brand-ink/5 border border-brand-ink/10 rounded-lg p-2.5">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${status.color}`}>
                {status.emoji} {status.label}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-brand-ink">{t.label}</div>
                <div className="text-[11px] text-brand-ink/65 mt-0.5 leading-snug">{t.why}</div>
                {t.link && (
                  <a
                    href={t.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-1 text-[11px] font-semibold text-teal hover:underline"
                  >
                    {t.linkLabel || 'Open'} ↗
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
